/**
 * Multi-device key sync — client-side opaque encryption helpers.
 *
 * The passphrase NEVER leaves the client. We derive a 256-bit AES-GCM
 * key via PBKDF2-SHA256 (600k iterations, OWASP 2023 recommendation)
 * and encrypt `{privateKey, publicKey, trustLedger, onboarded}`
 * client-side. The server only ever sees opaque ciphertext.
 */
import axios from 'axios';
import { setItem as safeSetItem } from './storage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KDF_ITERATIONS = 600_000;
const FORMAT_VERSION = 1;

const b64 = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)));

const fromB64 = (s) => {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

async function deriveKey(passphrase, saltBytes, iterations = KDF_ITERATIONS) {
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function gatherLocalPayload() {
  const keys = JSON.parse(window.localStorage.getItem('ghostcomm_keys') || '{}');
  // Collect every per-peer trust-ledger entry so restore recovers
  // the safety-stripe state on the new device.
  const trust = {};
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (!k) continue;
    if (k.startsWith('ghostcomm_seen_fingerprints_')) {
      try {
        trust[k] = JSON.parse(window.localStorage.getItem(k));
      } catch {
        /* ignore malformed rows */
      }
    }
  }
  return {
    format_version: FORMAT_VERSION,
    exported_at: new Date().toISOString(),
    keys,
    trust_ledger: trust,
    onboarded: window.localStorage.getItem('ghostcomm_onboarded') || null,
  };
}

/**
 * Encrypts the current device's keys + trust ledger and uploads to the
 * server. Returns the server's status row.
 * @param {string} passphrase — user-chosen, not their account password
 */
export async function uploadKeyBackup(passphrase) {
  if (!passphrase || passphrase.length < 10) {
    throw new Error('Sync passphrase must be at least 10 characters.');
  }
  const payload = gatherLocalPayload();
  if (!payload.keys || !payload.keys.privateKey) {
    throw new Error('No private key found on this device — cannot create backup.');
  }

  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);

  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext
  );

  const res = await axios.put(`${API}/users/me/key-backup`, {
    ciphertext_b64: b64(ciphertext),
    iv_b64: b64(iv),
    salt_b64: b64(salt),
    kdf_iterations: KDF_ITERATIONS,
    format_version: FORMAT_VERSION,
  });
  return res.data;
}

export async function getKeyBackupStatus() {
  const res = await axios.get(`${API}/users/me/key-backup/status`);
  return res.data;
}

export async function deleteKeyBackup() {
  const res = await axios.delete(`${API}/users/me/key-backup`);
  return res.data;
}

/**
 * Downloads the encrypted backup, decrypts with the passphrase, and
 * writes keys + trust ledger into localStorage. Returns a summary.
 * Caller should trigger a page reload so the AuthContext re-initialises
 * from the freshly-restored keys.
 */
export async function restoreKeyBackup(passphrase) {
  if (!passphrase) throw new Error('Passphrase is required');
  const res = await axios.get(`${API}/users/me/key-backup`);
  const { ciphertext_b64, iv_b64, salt_b64, kdf_iterations } = res.data;

  const key = await deriveKey(passphrase, fromB64(salt_b64), kdf_iterations);
  let plaintextBuf;
  try {
    plaintextBuf = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(iv_b64) },
      key,
      fromB64(ciphertext_b64)
    );
  } catch {
    // Wrong passphrase fails the GCM tag check here.
    throw new Error('Wrong sync passphrase.');
  }

  const payload = JSON.parse(new TextDecoder().decode(plaintextBuf));
  if (!payload?.keys?.privateKey) {
    throw new Error('Backup is malformed — no private key found.');
  }

  // Persist via safeSetItem so the integrity manifest stays consistent.
  safeSetItem(
    'ghostcomm_keys',
    JSON.stringify({
      publicKey: payload.keys.publicKey,
      privateKey: payload.keys.privateKey,
    })
  );
  if (payload.onboarded) {
    safeSetItem('ghostcomm_onboarded', payload.onboarded);
  }
  Object.entries(payload.trust_ledger || {}).forEach(([k, v]) => {
    if (k.startsWith('ghostcomm_seen_fingerprints_')) {
      safeSetItem(k, JSON.stringify(v));
    }
  });

  return {
    exported_at: payload.exported_at,
    format_version: payload.format_version,
    trust_entries: Object.keys(payload.trust_ledger || {}).length,
  };
}
